import { User } from "@types";
import { Button, Checkbox, Flex, Form, Input, Spin } from "antd";
import { useEffect, useState } from "react";
import { Translation, useTranslation } from "react-i18next";
import { useEditUser, useUserResetPassword } from "@hooks";
import { useGetRoles } from "../../hooks/roles/useGetRoles";
import { UserRole } from "../../types/User";
import { Lock } from "lucide-react";

interface Props {
  user: User;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UsersForm = ({ user, setIsModalOpen }: Props) => {
  const [userForm] = Form.useForm();
  const { t } = useTranslation();
  const editUser = useEditUser();
  const resetPassword = useUserResetPassword();
  const [userRoles, setUserRoles] = useState(user.applicationRoles.map((item: any) => item.id));
  const {data, isLoading} = useGetRoles();

  const translateRoles = (role: UserRole) => {
    switch (role.name) {
      case "Admin":
        return <Translation>{(t) => t("roles.admin")}</Translation>;
      case "DataAnalyst":
        return <Translation>{(t) => t("roles.dataAnalyst")}</Translation>;
      case "QuestionAdmin":
        return <Translation>{(t) => t("roles.questionAdmin")}</Translation>;
      case "TermAdmin":
        return <Translation>{(t) => t("roles.termAdmin")}</Translation>;
      case "ExamAdmin":
        return <Translation>{(t) => t("roles.examAdmin")}</Translation>;
      case "ExamParticipant":
        return <Translation>{(t) => t("roles.examParticipant")}</Translation>;
      case "Assessor":
        return <Translation>{(t) => t("roles.assessor")}</Translation>;
      default:
        return role.name;
    }
  }

  useEffect(() => {
    if (user) {
      userForm.setFieldsValue(user);
    }
  }, [user]);

  const onSubmit = (values: any) => {
    console.log(values);
    editUser.mutate({
      id: user.id, 
      applicationRoles: userRoles.map((role: string) => ({id: role})),
      ...values }, {
      onSuccess: () => {
        setIsModalOpen(false);
      }
    });
  }

  return (
    <Form layout="vertical" form={userForm} onFinish={onSubmit}>
      <Form.Item
        name={["firstName"]}
        label={t("table.name")}
        rules={[{ required: true, message: t("forms.user.firstNameDesc") }]}
      >
        <Input placeholder={t("table.name")} />
      </Form.Item>
      <Form.Item
        name={["lastName"]}
        label={t("table.lastName")}
        rules={[{ required: true, message: t("forms.user.lastNameDesc") }]}
      >
        <Input placeholder={t("table.lastName")} />
      </Form.Item>
      <Form.Item name={["oib"]} label={t("table.oib")}>
        <Input placeholder={t("forms.user.oib")} />
      </Form.Item>
      <Form.Item
        name={["email"]}
        label={t("table.email")}
        rules={[{ required: true, message: t("forms.user.emailDesc") }]}
      >
        <Input placeholder={t("table.email")} />
      </Form.Item>
      <Form.Item style={{textAlign: 'center'}}>
        <Spin spinning={isLoading}> 
          {data && (
            <Checkbox.Group value={userRoles} onChange={(e) => {
              setUserRoles(e as any);
            }}>
              {data.map((role: UserRole) => (
                <Checkbox value={role.id} key={role.id} >{translateRoles(role)}</Checkbox>
              ))}
              </Checkbox.Group>
          )}
        </Spin>
      </Form.Item>
      <Flex justify="space-between">
        {user.applicationRoles?.length === 1 && user.applicationRoles?.[0].name === "ExamParticipant" ? <div></div> : <Button loading={resetPassword.isPending} icon={<Lock/>} onClick={() => {
          resetPassword.mutate(user.id);
        }}>
          {t("forms.user.resetPassword")}
        </Button>}
        <Button
          type="primary"
          htmlType="submit"
          loading={editUser.isPending}
        >
          {t("forms.category.save")}
        </Button>
      </Flex>
    </Form>
  );
};
